import { useCallback } from 'react';
import { useAppDispatch } from '../../../../store';
import {
  EntityListProductInputData,
  EntityListTypeSupported,
  getEntityListSpecification,
} from '../entityListsLib';
import { EntityIdentificationType } from '@ydistri/api-sdk';
import { selectImportableItemParser } from '../../../../lib/imports/importLib';
import useEntityListImportFormat from './useEntityListImportFormat';
import { setImportedEntityListItems } from '../entityListAdministrationSlice';
import { useApplicationConfiguration } from '../../../../hooks/useApplicationConfiguration';
import { createImportedItemValidatorContext } from './useImportedItemValidatorContext';

export type ParseAndUpdateEntityListItemsFunction = (
  content: string,
  productIdentificationType: EntityIdentificationType,
  storeIdentificationType: EntityIdentificationType,
) => void;

/**
 * Hook that provides a function to parse entity list content and update the Redux state with parsed items.
 * This is useful when identification types change and the content needs to be revalidated.
 */
const useParseAndUpdateEntityListItems = (
  entityListType: EntityListTypeSupported,
): ParseAndUpdateEntityListItemsFunction => {
  const dispatch = useAppDispatch();
  const specification = getEntityListSpecification(entityListType);
  const inputFormat = useEntityListImportFormat(entityListType);
  const { mdbProductIdAutoIncrement, mdbWarehouseIdAutoIncrement } = useApplicationConfiguration();

  return useCallback(
    (
      content: string,
      productIdentificationType: EntityIdentificationType,
      storeIdentificationType: EntityIdentificationType,
    ) => {
      if (!content || content.trim().length === 0) {
        return;
      }

      // Use text/plain parser as the content is already in plain text format
      const parser = selectImportableItemParser<EntityListProductInputData>(['text/plain']);
      if (!parser) {
        return;
      }

      // Create validator context with the NEW identification types using the shared function
      const validatorContext = createImportedItemValidatorContext(
        productIdentificationType,
        storeIdentificationType,
        mdbProductIdAutoIncrement,
        mdbWarehouseIdAutoIncrement,
      );

      const validators = specification.getValidators(validatorContext);

      const parsedRows = parser.parse(content, inputFormat, validators);

      // Update the state with the newly parsed items
      dispatch(setImportedEntityListItems({ entityListType, data: parsedRows }));
    },
    [
      dispatch,
      entityListType,
      inputFormat,
      specification,
      mdbProductIdAutoIncrement,
      mdbWarehouseIdAutoIncrement,
    ],
  );
};

export default useParseAndUpdateEntityListItems;
